// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import React, { FC, useContext, useMemo } from "react";

const enum fields {
    showShopAllProductsLink = "showShopAllProductsLink",
    showVisitBrandWebsiteLink = "showVisitBrandWebsiteLink",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.showShopAllProductsLink]: boolean;
        [fields.showVisitBrandWebsiteLink]: boolean;
    };
}

type Props = OwnProps;

export interface ShopAllProductsLinkStyles {
    linksContainer?: GridContainerProps;
    productLinkItem?: GridItemProps;
    productLink?: LinkPresentationProps;
    externalLinkItem?: GridItemProps;
    externalLink?: LinkPresentationProps;
    fullWidthProductLinkItem?: GridItemProps;
}

export const shopAllProductsLinkStyles: ShopAllProductsLinkStyles = {};

const styles = shopAllProductsLinkStyles;

const ShopAllProductsLink: FC<Props> = ({ fields: { showShopAllProductsLink, showVisitBrandWebsiteLink } }) => {
    const { value: brand } = useContext(BrandStateContext);

    if (!brand || !(showShopAllProductsLink || showVisitBrandWebsiteLink)) {
        return null;
    }

    const showExternalLink = useMemo(() => {
        return brand.externalUrl && showVisitBrandWebsiteLink;
    }, [brand.externalUrl, showVisitBrandWebsiteLink]);

    return (
        <GridContainer {...styles.linksContainer}>
            {showShopAllProductsLink && (
                <GridItem {...(showExternalLink ? styles.productLinkItem : styles.fullWidthProductLinkItem)}>
                    <Link
                        data-test-selector="brandToProductPageLink"
                        href={brand.productListPagePath}
                        {...styles.productLink}
                        aria-label={translate("Shop All Products")}
                    >
                        {translate("Shop All Products")}
                    </Link>
                </GridItem>
            )}
            {showExternalLink && (
                <GridItem {...(showShopAllProductsLink ? styles.externalLinkItem : styles.fullWidthProductLinkItem)}>
                    <Link
                        data-test-selector="brandExternalUrlLink"
                        href={brand.externalUrl}
                        {...styles.externalLink}
                        aria-label={translate("Visit brand Website")}
                    >
                        {translate("Visit Brand Website")}
                    </Link>
                </GridItem>
            )}
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: ShopAllProductsLink,
    definition: {
        group: "Brand Details",
        displayName: "Shop All Products Link",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.showShopAllProductsLink,
                displayName: "Show Shop All Products Link",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showVisitBrandWebsiteLink,
                displayName: "Show Visit Brand Website Link",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
